import { useEffect, useRef, useState } from "react";

import { queryKeys } from "@/api/hooks";
import {
  classifyRunReadFailure,
  highestSequenceOf,
  usePreviewRunEventQuery,
  usePreviewRunEventsQuery,
} from "@/api/previewInsights";
import { QueryPendingState } from "@/api/QueryPendingState";
import { isTerminalQueryError } from "@/api/queryClient";
import { useCapability } from "@/auth/SessionContext";
import { buildAnxietyView } from "@/lib/anxietyView";
import {
  conversationTurns,
  currentPosition,
  readEvents,
  type ConversationTurn,
} from "@/lib/conversation";
import {
  describePosition,
  describeWithheld,
  positionsDiffer,
  readContentSlot,
  readInlineBody,
  type ContentSlot,
} from "@/lib/previewDecisions";
import {
  CAPTURE_STATE_LABELS,
  CONTENT_UNAVAILABLE_LABELS,
  canStillChange,
  describeView,
  type PreviewRunView,
} from "@/lib/previewInsights";
import { Button } from "@/ui/Button";
import { EmptyState, StaleDataNotice } from "@/ui/states";

import { AnxietyView } from "./AnxietyView";
import { PreviewRunReadFailure } from "./PreviewRunReadFailure";
import { SimStatsPanel } from "./SimStatsPanel";

/**
 * Conversation — the run page's first tab (Preview Insights design §10.3).
 *
 * What it shows, all of it read from the run's own `turn` events:
 *
 *  - WHERE THE RUNTIME IS: the agent and track (and phase and step) after the
 *    latest turn, the producer's own account, never inferred here;
 *  - for a run a patient simulator drove, the simulator's body-free stats;
 *  - the Anxiety view: classification and threshold events grouped by unit;
 *  - the transcript: each turn's patient input and Liv response.
 *
 * ## Text arrives on the clinician's say-so
 *
 * The compact event page carries every turn's bodies BY REFERENCE. The words
 * come from the one body-bearing read (`usePreviewRunEventQuery`), which the
 * Editor Service audits as "full detail viewed" — so the transcript asks for
 * them once the clinician presses `Show conversation text`, then for each turn
 * the run lists, and for turns that arrive later in a live run. Nothing is
 * requested for an event the run did not list.
 *
 * ## Text only
 *
 * A body is rendered as text, never as markup, with `dir="auto"` so a Hebrew
 * line sets its own direction inside an English page. A body the producer
 * withheld says why and shows nothing; a structured (JSON) body is shown as
 * its text.
 */
export function PreviewRunConversation({
  projectId,
  previewRunRef,
  view,
  active,
  paused,
  pollIntervalMs,
  focusEventRef,
}: {
  projectId: string;
  previewRunRef: string;
  view: PreviewRunView;
  /** Whether this tab is the selected one. The read starts the first time it is. */
  active: boolean;
  paused: boolean;
  pollIntervalMs: number;
  /** The turn event the address asks this tab to open on, already shape-checked; null for none. */
  focusEventRef: string | null;
}) {
  const inspect = useCapability("preview.inspect");
  const [opened, setOpened] = useState(active);
  if (active && !opened) {
    setOpened(true);
  }
  const readable =
    view.kind === "live" ||
    view.kind === "completed" ||
    view.kind === "incomplete";
  const events = usePreviewRunEventsQuery(projectId, previewRunRef, {
    enabled: readable && opened,
    pollIntervalMs:
      active && !paused && canStillChange(view) ? pollIntervalMs : false,
  });

  // CATCH UP TO THE ENVELOPE. The envelope and the event page are polled on two
  // timers, so the envelope can observe the run's end and stop this one before
  // the tick that would have read the final turns. One more read follows when
  // the page holds less than the envelope says the run reached — and only when
  // that read STARTED before the envelope reached it; what a later read still
  // lacks is a declared gap, which re-reading cannot fill (the Flow & Decisions
  // tab reasons the same way, and shares this query).
  const envelopeLast = view.summary?.lastSequence ?? null;
  const loadedLast = events.data?.content
    ? (highestSequenceOf(events.data.content.events) ?? 0)
    : null;
  const { refetch, isFetching, isError } = events;
  const envelopeSeenIdle = useRef<number | null>(envelopeLast);
  const envelopeAtStart = useRef<number | null>(null);
  const wasFetching = useRef(false);
  useEffect(() => {
    if (isFetching) {
      if (!wasFetching.current) {
        envelopeAtStart.current = envelopeSeenIdle.current;
      }
    } else {
      envelopeSeenIdle.current = envelopeLast;
    }
    wasFetching.current = isFetching;
  }, [isFetching, envelopeLast]);
  useEffect(() => {
    if (!active || paused || !readable || !opened || isFetching || isError) {
      return;
    }
    if (
      loadedLast === null ||
      envelopeLast === null ||
      loadedLast >= envelopeLast
    ) {
      return;
    }
    const known = envelopeAtStart.current;
    if (known !== null && known >= envelopeLast) {
      return;
    }
    void refetch();
  }, [
    active,
    paused,
    readable,
    opened,
    isFetching,
    isError,
    loadedLast,
    envelopeLast,
    refetch,
  ]);

  const simPanel = (
    <SimStatsPanel
      projectId={projectId}
      previewRunRef={previewRunRef}
      enabled={inspect.allowed && opened}
    />
  );

  if (!readable) {
    return (
      <div className="space-y-4">
        {simPanel}
        {view.kind === "waiting" ? (
          <EmptyState
            title="No conversation recorded yet"
            description={`${describeView(view)} Turns appear here once the runtime records one.`}
          />
        ) : (
          <EmptyState
            title="The conversation cannot be shown"
            description="The trace for this run is unavailable, so its conversation cannot be read. The run status above says why; nothing is inferred in its place."
          />
        )}
      </div>
    );
  }

  if (events.isPending || !events.isFetchedAfterMount) {
    return (
      <QueryPendingState
        isPaused={events.isPaused}
        failureReason={events.failureReason}
        label="Loading conversation"
        errorTitle="This preview run could not be read"
        rows={4}
        queryKey={queryKeys.previewRunEvents(projectId, previewRunRef)}
      />
    );
  }

  if (events.isError && (!events.data || isTerminalQueryError(events.error))) {
    return (
      <PreviewRunReadFailure
        failure={classifyRunReadFailure(events.error)}
        error={events.error}
        onRetry={() => void events.refetch()}
      />
    );
  }

  const reading = events.data;
  const content = reading.content;
  if (content === null) {
    const reason = reading.run.contentUnavailableReason ?? null;
    return (
      <div className="space-y-4">
        {simPanel}
        <EmptyState
          title="The conversation cannot be shown"
          description={`The recorded preview target reports no readable content for this run right now${
            reason ? `: ${CONTENT_UNAVAILABLE_LABELS[reason]}` : ""
          } (capture: ${CAPTURE_STATE_LABELS[reading.run.captureState].toLowerCase()}). Nothing is inferred in its place.`}
        />
      </div>
    );
  }

  return (
    <ConversationBody
      projectId={projectId}
      previewRunRef={previewRunRef}
      records={content.events}
      complete={content.complete}
      stale={events.isError}
      staleAt={events.dataUpdatedAt}
      onRetry={() => void events.refetch()}
      active={active}
      focusEventRef={focusEventRef}
      simPanel={simPanel}
    />
  );
}

function ConversationBody({
  projectId,
  previewRunRef,
  records,
  complete,
  stale,
  staleAt,
  onRetry,
  active,
  focusEventRef,
  simPanel,
}: {
  projectId: string;
  previewRunRef: string;
  records: readonly unknown[];
  complete: boolean;
  stale: boolean;
  staleAt: number;
  onRetry: () => void;
  active: boolean;
  focusEventRef: string | null;
  simPanel: React.ReactNode;
}) {
  const [textRequested, setTextRequested] = useState(false);
  const container = useRef<HTMLDivElement>(null);
  const { events, unreadable } = readEvents(records);
  const turns = conversationTurns(events);
  const position = currentPosition(turns);
  const anxiety = buildAnxietyView(events);
  const focusTarget =
    focusEventRef !== null &&
    turns.some((turn) => turn.eventRef === focusEventRef)
      ? focusEventRef
      : null;

  // THE ADDRESS OPENS ONE TURN: when the link named a turn this run lists,
  // focus lands on it. A reference the run does not list moves focus nowhere
  // and is stated below; nothing is requested for it.
  useEffect(() => {
    if (!active || focusTarget === null) {
      return;
    }
    const target = container.current?.querySelector<HTMLElement>(
      `[data-event-ref="${focusTarget}"]`,
    );
    if (target) {
      target.focus();
      if (typeof target.scrollIntoView === "function") {
        target.scrollIntoView({ block: "center" });
      }
    }
  }, [active, focusTarget, turns.length]);

  return (
    <div ref={container} className="space-y-6">
      <StaleDataNotice updatedAt={staleAt} onRetry={onRetry} visible={stale} />

      {!complete ? (
        <p
          role="status"
          className="rounded-md border border-border bg-muted/40 px-4 py-3 text-sm text-foreground"
        >
          Only the events read so far are shown: the run kept reporting more
          after its cursor stopped advancing, or after more events than the
          producer contract allows had been served.
        </p>
      ) : null}

      {focusEventRef !== null && focusTarget === null ? (
        <p
          role="status"
          className="rounded-md border border-border bg-muted/40 px-4 py-3 text-sm text-foreground"
        >
          The event named in this link is not among this run&apos;s recorded
          turns. Nothing was requested for it.
        </p>
      ) : null}

      <section aria-labelledby="current-position-heading" className="space-y-1">
        <h3 id="current-position-heading" className="text-base font-semibold">
          Current position
        </h3>
        {position === null ? (
          <p className="text-sm text-muted-foreground">
            No turn has been recorded yet, so the runtime has no position to
            report.
          </p>
        ) : (
          <p className="text-sm text-foreground">
            <span dir="auto" data-testid="current-position">
              {describePosition(position)}
            </span>
            <span className="ms-2 text-muted-foreground">
              after turn{" "}
              <span dir="ltr">
                {turns[turns.length - 1]?.coordinate.turnNumber ?? turns.length}
              </span>
            </span>
          </p>
        )}
      </section>

      {simPanel}

      <AnxietyView units={anxiety} />

      <section aria-labelledby="transcript-heading" className="space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h3 id="transcript-heading" className="text-base font-semibold">
            Conversation
          </h3>
          {turns.length > 0 && !textRequested ? (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setTextRequested(true)}
            >
              Show conversation text
            </Button>
          ) : null}
        </div>
        {turns.length === 0 ? (
          <EmptyState
            title="No turns recorded yet"
            description="The trace holds no turn yet. The conversation appears here once the runtime records one."
          />
        ) : (
          <>
            {!textRequested ? (
              <p className="text-sm text-muted-foreground">
                {turns.length} turn{turns.length === 1 ? "" : "s"} recorded.
                The text of each is read from the recorded preview target when
                you ask for it.
              </p>
            ) : null}
            <ol className="space-y-3">
              {turns.map((turn, index) => (
                <TurnCard
                  key={turn.eventRef}
                  projectId={projectId}
                  previewRunRef={previewRunRef}
                  turn={turn}
                  position={index + 1}
                  textRequested={textRequested}
                  focused={turn.eventRef === focusTarget}
                />
              ))}
            </ol>
          </>
        )}
        {unreadable > 0 ? (
          <p role="status" className="text-sm text-muted-foreground">
            {unreadable} record{unreadable === 1 ? "" : "s"} on this
            run&apos;s event page could not be read by this build.
          </p>
        ) : null}
      </section>
    </div>
  );
}

function TurnCard({
  projectId,
  previewRunRef,
  turn,
  position,
  textRequested,
  focused,
}: {
  projectId: string;
  previewRunRef: string;
  turn: ConversationTurn;
  position: number;
  textRequested: boolean;
  focused: boolean;
}) {
  const { userInput, livResponse, before, after } = turn.detail;
  // Bodies a compact page already carries inline need no read.
  const inlineAlready =
    (userInput === null || userInput.state !== "reference") &&
    (livResponse === null || livResponse.state !== "reference");
  const detail = usePreviewRunEventQuery(
    projectId,
    previewRunRef,
    turn.eventRef,
    { enabled: textRequested && !inlineAlready },
  );

  const fetched = detail.data?.event;
  const fetchedDetail =
    typeof fetched === "object" && fetched !== null
      ? (fetched as Record<string, unknown>)["detail"]
      : undefined;
  const fromDetail = (name: "userInput" | "livResponse"): ContentSlot | null =>
    typeof fetchedDetail === "object" &&
    fetchedDetail !== null &&
    !Array.isArray(fetchedDetail)
      ? readContentSlot((fetchedDetail as Record<string, unknown>)[name])
      : null;
  const patientSlot = inlineAlready ? userInput : fromDetail("userInput");
  const livSlot = inlineAlready ? livResponse : fromDetail("livResponse");

  const turnNumber = turn.coordinate.turnNumber ?? position;
  return (
    <li
      data-event-ref={turn.eventRef}
      tabIndex={-1}
      className="space-y-2 rounded-md border border-border bg-card p-3 focus-visible:outline-2 focus-visible:outline-ring"
      {...(focused ? { "aria-current": "true" as const } : {})}
    >
      <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
        Turn <span dir="ltr">{turnNumber}</span>
        {positionsDiffer(before, after) ? (
          <span className="ms-2 font-normal normal-case tracking-normal">
            <span dir="auto">{describePosition(before)}</span> →{" "}
            <span dir="auto">{describePosition(after)}</span>
          </span>
        ) : null}
      </p>
      {textRequested ? (
        detail.isPending && !inlineAlready ? (
          <p className="text-sm text-muted-foreground">Reading this turn…</p>
        ) : detail.isError && !inlineAlready ? (
          <p role="alert" className="text-sm text-destructive">
            This turn&apos;s text could not be read
            {describeDetailFailure(detail.error)}.{" "}
            <button
              type="button"
              className="underline underline-offset-2"
              onClick={() => void detail.refetch()}
            >
              Try again
            </button>
          </p>
        ) : (
          <div className="space-y-2">
            <Line label="Patient" slot={patientSlot} />
            <Line label="Liv" slot={livSlot} />
          </div>
        )
      ) : null}
    </li>
  );
}

export function Line({ label, slot }: { label: string; slot: ContentSlot | null }) {
  const body = slot === null ? null : readInlineBody(slot);
  const withheld = slot === null ? null : describeWithheld(slot);
  return (
    <div>
      <p className="text-xs font-medium text-muted-foreground">{label}</p>
      {body !== null ? (
        <p
          dir="auto"
          className="whitespace-pre-wrap break-words text-sm text-foreground"
        >
          {body.kind === "text"
            ? body.text
            : JSON.stringify(body.value, null, 2)}
        </p>
      ) : (
        <p className="text-sm text-muted-foreground">
          {slot === null
            ? "Not recorded for this turn."
            : (withheld ?? "Not carried by this read.")}
        </p>
      )}
    </div>
  );
}

export function describeDetailFailure(error: unknown): string {
  const failure = classifyRunReadFailure(error);
  switch (failure.kind) {
    case "not_found":
      return ": the run no longer answers for this event";
    case "denied":
      return ": your account is not authorized to read it";
    case "upstream":
      return failure.recovery === "retry"
        ? ": the preview target took too long to answer"
        : ": the preview target could not answer";
    case "error":
      return ` (${failure.code})`;
    case "transport":
      return ": the Editor Service could not be reached";
    case "unauthenticated":
    case "not_implemented":
      return "";
  }
}
